import type { ImportedInventoryItem } from '../Types';

const inventoryItemKey = (name: string, unit: string) =>
  `${name.trim().toLowerCase()}|${(unit || 'unit').trim().toLowerCase()}`;

const confidenceRank = { high: 3, medium: 2, low: 1 } as const;

const mergeImportedItems = (
  a: ImportedInventoryItem,
  b: ImportedInventoryItem
): ImportedInventoryItem => {
  const preferB =
    confidenceRank[b.confidence] > confidenceRank[a.confidence];
  const base = preferB ? b : a;
  const other = preferB ? a : b;

  const qtyA = a.item.quantityOnHand;
  const qtyB = b.item.quantityOnHand;
  let quantityOnHand: number | undefined;
  if (qtyA != null && qtyB != null) quantityOnHand = qtyA + qtyB;
  else if (qtyA != null) quantityOnHand = qtyA;
  else if (qtyB != null) quantityOnHand = qtyB;

  const minA = a.item.minQuantity;
  const minB = b.item.minQuantity;
  let minQuantity: number | undefined;
  if (minA != null && minB != null) minQuantity = Math.max(minA, minB);
  else if (minA != null) minQuantity = minA;
  else if (minB != null) minQuantity = minB;

  const fromFileA =
    !a.categorySuggested && Boolean(a.categoryName?.trim());
  const fromFileB =
    !b.categorySuggested && Boolean(b.categoryName?.trim());
  const categoryName =
    (fromFileA ? a.categoryName : null)?.trim() ||
    (fromFileB ? b.categoryName : null)?.trim() ||
    a.categoryName?.trim() ||
    b.categoryName?.trim() ||
    null;

  const categorySuggested =
    Boolean(categoryName) &&
    !fromFileA &&
    !fromFileB &&
    (a.categorySuggested || b.categorySuggested);

  return {
    ...base,
    item: {
      ...base.item,
      ...(quantityOnHand != null ? { quantityOnHand } : {}),
      ...(minQuantity != null ? { minQuantity } : {}),
      unitMultiplier: base.item.unitMultiplier ?? other.item.unitMultiplier,
      baseUnit: base.item.baseUnit ?? other.item.baseUnit,
      internalSku: base.item.internalSku ?? other.item.internalSku,
      note: base.item.note ?? other.item.note,
      imageUrl: base.item.imageUrl ?? other.item.imageUrl,
      supplierUserId: base.item.supplierUserId ?? other.item.supplierUserId,
      categoryId: base.item.categoryId ?? other.item.categoryId
    },
    categoryName,
    categorySuggested,
    needsReview: a.needsReview || b.needsReview,
    rawSnippet: a.rawSnippet || b.rawSnippet
  };
};

/** Merge rows that share the same name + unit (case-insensitive). */
export const dedupeImportedInventoryItems = (
  items: ImportedInventoryItem[]
): { items: ImportedInventoryItem[]; mergedCount: number } => {
  const byKey = new Map<string, ImportedInventoryItem>();
  let mergedCount = 0;

  for (const row of items) {
    const name = row.item?.name?.trim() ?? '';
    if (!name) continue;
    const key = inventoryItemKey(name, row.item?.unit ?? 'unit');
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      continue;
    }
    mergedCount += 1;
    byKey.set(key, mergeImportedItems(existing, row));
  }

  return { items: [...byKey.values()], mergedCount };
};

export const importedInventoryItemKey = (row: ImportedInventoryItem): string =>
  inventoryItemKey(row.item?.name ?? '', row.item?.unit ?? 'unit');

export const draftInventoryItemKey = (name: string, unit: string): string =>
  inventoryItemKey(name, unit);
